import React, { useMemo } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import '../../../Styles/PickScoreStyle.css';

import { getLogoUrl } from '../../../utils/getLogoUrl';
import { sanitizeScoreInput, isTiedScorePick } from '../../../lib/scoreValidation';
import { withNormalizedColors } from '../../../lib/teamColor';

const atIcon = '/logos/atIcon.png';

const LIGHT_NAMED_COLORS = ['white', 'ivory', 'snow', 'floralwhite', 'ghostwhite', 'honeydew', 'mintcream', 'azure', 'aliceblue', 'lavenderblush', 'seashell', 'cornsilk', 'lemonchiffon', 'lightyellow', 'beige', 'oldlace', 'linen', 'antiquewhite', 'papayawhip', 'blanchedalmond', 'bisque', 'peachpuff', 'navajowhite', 'moccasin', 'wheat', 'mistyrose', 'lavender'];

const isLightColor = (color) => {
    if (!color) return true;
    if (LIGHT_NAMED_COLORS.includes(color.toLowerCase())) return true;
    if (!color.startsWith('#')) return false;
    const clean = color.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.75;
};

const getAccentColor = (team) => {
    if (!team.color) return '#333';
    if (!isLightColor(team.color)) return team.color;
    if (team.fontColor && !isLightColor(team.fontColor)) return team.fontColor;
    return '#333';
};

const PickScore = (rawProps) => {
    // Repair hand-typed team colors (e.g. a hex code saved without its "#")
    // before they reach inline styles — an invalid CSS color is silently
    // dropped and the team's text becomes invisible. See lib/teamColor.
    const props = {
        ...rawProps,
        homeTeam: withNormalizedColors(rawProps.homeTeam),
        awayTeam: withNormalizedColors(rawProps.awayTeam),
    };
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // This card's pick, derived straight from the `pick` prop. Each card gets
    // only its own pick (not the whole week's picks), so React.memo skips
    // re-rendering every other card while one score box is being typed in.
    const { pick, isPickScore } = rawProps;
    const pickForGame = useMemo(() => {
        if (!pick) return {};
        // Defensive: sanitize any legacy pick-score values loaded from
        // Firestore so a previously-saved "-1" can't redisplay or resave.
        if (!isPickScore) return pick;
        return {
            ...pick,
            awayScore: sanitizeScoreInput(pick.awayScore),
            homeScore: sanitizeScoreInput(pick.homeScore),
        };
    }, [pick, isPickScore]);
    
    const dateOptions = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    };

    // Report only what changed; the parent merges it into a NEW pick object
    // (never mutating the old one in place, which a memoized card would miss).
    const savePicks = (e) => {
        const { name, value } = e.target;
        let changes = null;
        if (props.isPickScore) {
            if (name === 'awayScore' || name === 'homeScore') {
                changes = { [name]: sanitizeScoreInput(value) };
            }
        } else if (name === 'awayCheck') {
            changes = { awayWin: true, homeWin: false };
        } else if (name === 'homeCheck') {
            changes = { awayWin: false, homeWin: true };
        }
        if (changes) props.onPickChange(props.gameId, changes);
    };

    return (
        <div className="container">
            {/* Pick the Score games that also pay the Pick the Winner .25
                (see the FAQ). Outside .subtext so it shows on phones too. */}
            {props.isPickScore && props.awardWinnerBonus && (
                <div className="scoring-tag" title="Pick the Score game that also counts as Pick the Winner: +.25 if the team your score has winning wins">
                    Score + Winner
                </div>
            )}
            <div className="subtext">
                    <div className='location'>
                        <span>{
                        new Date((props.startTime?.seconds || 0) * 1000).toLocaleString('en', dateOptions)}</span>
                        <span>{props.neutralSite || props.homeTeam.stadium}</span>
                    </div>
                    <div className='vegas'>
                        <span>Vegas Line</span>
                        <span>{props.vegasLine}</span>
                    </div>
                </div>
            <div className='scoreCard'>
                <div className='away' style={{backgroundColor: props.awayTeam.color, color: props.awayTeam.fontColor}}>
                    {props.isPickScore ?
                        <input className="score" type='text' inputMode='numeric' pattern='[0-9]*' placeholder='SCORE' name='awayScore' onChange={savePicks}  value={pickForGame.awayScore} disabled={props.disabled}/> :
                        <input className="check" type='checkbox' name='awayCheck' onChange={savePicks} checked={pickForGame.awayWin} disabled={props.disabled}/>
                    }
                    <img src={getLogoUrl(props.awayTeam.logo)} alt='away Logo' />
                    <div className='teamInfo'>
                        <div className='teamName'>
                            {props.awayRanking && '#' + props.awayRanking} {props.awayTeam.name}
                        </div>
                        <div className='conference'>
                            {props.awayRecord?.trim() && <span className='record'>({props.awayRecord}) </span>}
                            {props.awayTeam.conference}
                        </div>
                    </div>
                </div>
                {props.neutralSite ? (
                    <div className='versus-badge'>VS</div>
                ) : (
                    <img className='versus' src={atIcon} alt='at icon' />
                )}
                <div className='home' style={{backgroundColor: props.homeTeam.color, color: props.homeTeam.fontColor}}>
                    <div className='teamInfo'>
                        <div className='teamName'>
                            {props.homeRanking && '#'+props.homeRanking} {props.homeTeam.name}
                        </div>
                        <div className='conference'>
                            {props.homeRecord?.trim() && <span className='record'>({props.homeRecord}) </span>}
                            {props.homeTeam.conference}
                        </div>
                    </div>
                    <img src={getLogoUrl(props.homeTeam.logo)} alt='home Logo' />
                    {props.isPickScore ?
                        <input className="score" type='text' inputMode='numeric' pattern='[0-9]*' placeholder='SCORE' name='homeScore' onChange={savePicks} value={pickForGame.homeScore} disabled={props.disabled}/> :
                        <input className="check" name='homeCheck' type='checkbox' onChange={savePicks} checked={pickForGame.homeWin} disabled={props.disabled}/>
                    }
                </div>
            </div>
            {props.isPickScore && !props.disabled && isTiedScorePick(pickForGame) && (
                <div className="tie-warning" role="alert">
                    Ties can&apos;t win. Pick a score with a winner.
                </div>
            )}
            {isMobile && (
                <details className="mobile-details">
                    <summary>Game Details</summary>
                    <div className="mobile-details-content">
                        <div className="mobile-details-game-info">
                            <span>{new Date((props.startTime?.seconds || 0) * 1000).toLocaleString('en', dateOptions)}</span>
                            <span>{props.neutralSite || props.homeTeam.stadium}</span>
                            <span className="mobile-vegas-line">{props.vegasLine}</span>
                        </div>
                        <div className="mobile-details-teams">
                            <div className="mobile-team-col" style={{ borderLeftColor: getAccentColor(props.awayTeam) }}>
                                <span className="mobile-team-name">
                                    {props.awayRanking && <span className="mobile-team-ranking">#{props.awayRanking}</span>}
                                    {props.awayTeam.name}
                                </span>
                                {props.awayRecord?.trim() && <span className="mobile-team-record">{props.awayRecord}</span>}
                                <span className="mobile-team-conf">{props.awayTeam.conference}</span>
                            </div>
                            <div className="mobile-team-col mobile-team-col-right" style={{ borderRightColor: getAccentColor(props.homeTeam) }}>
                                <span className="mobile-team-name">
                                    {props.homeRanking && <span className="mobile-team-ranking">#{props.homeRanking}</span>}
                                    {props.homeTeam.name}
                                </span>
                                {props.homeRecord?.trim() && <span className="mobile-team-record">{props.homeRecord}</span>}
                                <span className="mobile-team-conf">{props.homeTeam.conference}</span>
                            </div>
                        </div>
                    </div>
                </details>
            )}
        </div>
    );
}

export default React.memo(PickScore);
